import { useState, type FormEvent } from 'react';
import { api, describeError, useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { Empty } from '../../ui/kit.tsx';
import { MerchantPicker, SiteSelect } from './pickers.tsx';
import { ORIGIN_LABELS, type DeeplinkTest, type Named, type NetworkDeeplinks, type ProgramList } from './types.ts';

/** Dry run of the generator: nothing is stored. */
export function DeeplinkTester({ data }: { data: NetworkDeeplinks }) {
  const { query } = useRouter();
  const programs = useOp<ProgramList>('admin.programs.list', { networkRef: data.network.ref, limit: 200 });
  const [siteRef, setSiteRef] = useState(data.sites[0]?.ref ?? '');
  const [programRef, setProgramRef] = useState(query.get('program') ?? '');
  const [merchant, setMerchant] = useState<Named | null>(null);
  const [targetUrl, setTargetUrl] = useState('');
  const [couponRef, setCouponRef] = useState('');
  const [callApi, setCallApi] = useState(false);
  const [result, setResult] = useState<DeeplinkTest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const site = data.sites.find((s) => s.ref === siteRef);

  const run = async (event: FormEvent) => {
    event.preventDefault();
    setRunning(true);
    setError(null);
    try {
      setResult(
        await api.op<DeeplinkTest>('admin.deeplinks.test', {
          siteRef,
          ...(programRef ? { programRef } : { networkRef: data.network.ref }),
          ...(merchant ? { merchantRef: merchant.ref } : {}),
          ...(targetUrl.trim() ? { targetUrl: targetUrl.trim() } : {}),
          ...(couponRef.trim() ? { couponRef: couponRef.trim() } : {}),
          callNetworkApi: callApi,
        }),
      );
    } catch (err) {
      setResult(null);
      setError(describeError(err));
    } finally {
      setRunning(false);
    }
  };

  if (data.sites.length === 0) return <Empty>You have no sites to test links for.</Empty>;
  return (
    <div className="aff-steps">
      <form className="card" onSubmit={run} aria-label="Deeplink test">
        <h2>Build a test link</h2>
        <div className="form-grid">
          <SiteSelect sites={data.sites} value={siteRef} onChange={setSiteRef} />
          <label>
            Program
            <select value={programRef} onChange={(e) => setProgramRef(e.target.value)}>
              <option value="">— none, pick a merchant —</option>
              {programs.data?.rows.map((p) => (
                <option key={p.ref} value={p.ref}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          {!programRef ? <MerchantPicker value={merchant} onChange={setMerchant} market={site?.country} /> : null}
          <label className="span-2">
            Target URL <small className="muted">blank: the coupon’s deep URL or the merchant homepage</small>
            <input type="url" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://www.example-shop.co.uk/sale" />
          </label>
          <label>
            Coupon ref <small className="muted">optional</small>
            <input className="mono" value={couponRef} onChange={(e) => setCouponRef(e.target.value)} />
          </label>
          <label className="inline-check" style={{ alignSelf: 'end' }}>
            <input type="checkbox" checked={callApi} onChange={(e) => setCallApi(e.target.checked)} /> Call the network API if the link builder needs it
          </label>
        </div>
        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={running || !siteRef}>
            {running ? 'Building…' : 'Build link'}
          </button>
          <span className="muted small">Dry run: nothing is stored.</span>
        </div>
      </form>
      <div className="card" aria-live="polite">
        <h2>Result</h2>
        {error ? <p className="text-warn">{error}</p> : null}
        {!result && !error ? <p className="muted">Pick a site and a program or merchant, then build.</p> : null}
        {result ? <TestResult result={result} /> : null}
      </div>
    </div>
  );
}

function TestResult({ result }: { result: DeeplinkTest }) {
  const marked = result.trackingUrl ? result.trackingUrl.split(result.clickRefPlaceholder) : [];
  return (
    <>
      <p className="muted small">
        {result.network.name} · {result.site.name}
        {result.merchant ? ` · ${result.merchant.name}` : ''}
        {result.program ? ` · ${result.program.name}` : ''}
      </p>
      {result.ok && result.trackingUrl ? (
        <>
          <div className="aff-url" data-tracking-url>
            {marked.map((part, i) => (
              <span key={i}>
                {part}
                {i < marked.length - 1 ? <mark title="Replaced with the click id at clickout">{result.clickRefPlaceholder}</mark> : null}
              </span>
            ))}
          </div>
          <p className="muted small">The highlighted placeholder becomes the click id when a visitor clicks out.</p>
        </>
      ) : result.generated ? (
        <p className="text-warn">Could not build a link: {result.error}</p>
      ) : (
        <p className="muted">Link not built (see warnings).</p>
      )}
      {result.warnings.length ? (
        <ul className="warnings">
          {result.warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      ) : null}
      <table className="table">
        <thead>
          <tr>
            <th scope="col">Parameter</th>
            <th scope="col">Value</th>
            <th scope="col">From</th>
          </tr>
        </thead>
        <tbody>
          {result.parameters.map((p) => (
            <tr key={p.parameter} className={p.requiredMissing ? 'text-warn' : undefined}>
              <td>
                <span className="mono">{p.parameter}</span> <small className="muted">{p.levelLabel}</small>
              </td>
              <td className="mono truncate" style={{ maxWidth: 260 }} title={p.value ?? ''}>
                {p.origin === 'deep_url' ? (result.deepUrl ?? '—') : p.origin === 'fallback_url' ? (result.fallbackUrl ?? p.value ?? '—') : (p.value ?? (p.requiredMissing ? 'missing' : '—'))}
              </td>
              <td>
                {ORIGIN_LABELS[p.origin] ?? p.origin}
                {p.scope ? <small className="muted"> · {p.scope.label}</small> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
